// Auth Slice - Backend API integration
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { AuthState, User } from '@/types';

const getInitialAuthState = (): Omit<AuthState, 'isLoading' | 'error'> => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isProxyInitializing: false,
  isSetupRequested: false,
  proxyError: null,
  isReadyToTrade: false,
});

const initialState: AuthState = {
  ...getInitialAuthState(),
  isLoading: true,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
    },
    setTokens: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string; provider?: 'backend' }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      // Note: we don't set isLoading to false here because getStatus might still be fetching
    },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      state.isSetupRequested = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setProxyInitializing: (state, action: PayloadAction<boolean>) => {
      state.isProxyInitializing = action.payload;
      if (action.payload) state.proxyError = null;
    },
    setProxyError: (state, action: PayloadAction<string | null>) => {
      state.proxyError = action.payload;
      state.isProxyInitializing = false;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSetupRequested: (state, action: PayloadAction<boolean>) => {
      state.isSetupRequested = action.payload;
    },
    setIsReadyToTrade: (state, action: PayloadAction<boolean>) => {
      state.isReadyToTrade = action.payload;
    },
  },
});

export const {
  setUser,
  setTokens,
  setAccessToken,
  clearAuth,
  setLoading,
  updateUser,
  setProxyInitializing,
  setProxyError,
  setError,
  clearError,
  setSetupRequested,
  setIsReadyToTrade,
} = authSlice.actions;
export default authSlice.reducer;
